import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Circle, ChevronRight, X } from 'lucide-react';
import { useEffect, useState } from 'react';

/**
 * GettingStartedChecklist — a compact widget that helps a first-time user
 * complete the 3–5 steps to activate a feature area.
 *
 * The widget is **persistent**: completion state is kept in localStorage
 * under `gs-checklist:<storageKey>` so the user never sees the same step
 * twice once done. A "Hide" button also lets them dismiss the whole
 * checklist forever (`gs-checklist-hide:<storageKey>`).
 *
 * Props:
 *   title          — H3 text above the steps
 *   storageKey     — unique per page (e.g. "banking-hub-v1")
 *   steps          — [{key, label, href?, onClick?, cta?, auto?}]
 *                     auto: true means mark complete when `isCompleted(step)`
 *                     returns true on render (e.g. "has 1+ bank account").
 *   isCompleted    — optional (step) => boolean for auto-completion
 *   className      — wrapper className overrides
 */
export default function GettingStartedChecklist({
  title = 'Getting started',
  storageKey,
  steps = [],
  isCompleted,
  className = '',
}) {
  const hideKey = `gs-checklist-hide:${storageKey}`;
  const stateKey = `gs-checklist:${storageKey}`;

  const [hidden, setHidden] = useState(() => localStorage.getItem(hideKey) === '1');
  const [completedMap, setCompletedMap] = useState(() => {
    try { return JSON.parse(localStorage.getItem(stateKey) || '{}'); } catch { return {}; }
  });

  // Sync completedMap back to localStorage on every change.
  useEffect(() => {
    localStorage.setItem(stateKey, JSON.stringify(completedMap));
  }, [completedMap, stateKey]);

  // Merge auto-completed steps whenever isCompleted() says so.
  useEffect(() => {
    if (!isCompleted) return;
    setCompletedMap((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const step of steps) {
        if (step.auto && !next[step.key] && isCompleted(step)) {
          next[step.key] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [steps, isCompleted]);

  if (hidden) return null;

  const done = steps.filter((s) => completedMap[s.key]).length;
  const total = steps.length;
  if (done === total && total > 0) {
    // All done — auto-hide after the user next reloads so the page is
    // clean. For this session we show a tiny "all done" chip.
    return (
      <Card className={`border-emerald-200 bg-emerald-50/50 ${className}`} data-testid={`gs-${storageKey}`}>
        <CardContent className="p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={16} className="text-emerald-600" />
            <span className="text-sm font-medium text-emerald-900">{title} — all done 🎉</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-emerald-700"
            onClick={() => { localStorage.setItem(hideKey, '1'); setHidden(true); }}
            data-testid={`gs-${storageKey}-hide`}
            title="Hide checklist"
          >
            <X size={12} />
          </Button>
        </CardContent>
      </Card>
    );
  }

  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const markDone = (key) => setCompletedMap((p) => ({ ...p, [key]: true }));

  return (
    <Card className={`border-violet-200 bg-gradient-to-br from-violet-50/50 to-transparent ${className}`} data-testid={`gs-${storageKey}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center justify-between">
          <span className="flex items-center gap-2">
            <span className="text-violet-700">{title}</span>
            <span className="text-[10px] font-normal text-muted-foreground">{done}/{total} · {pct}%</span>
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground"
            onClick={() => { localStorage.setItem(hideKey, '1'); setHidden(true); }}
            data-testid={`gs-${storageKey}-hide`}
            title="Hide checklist"
          >
            <X size={12} />
          </Button>
        </CardTitle>
        <div className="h-1 w-full bg-violet-100 rounded-full overflow-hidden mt-1">
          <div className="h-full bg-violet-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-1.5">
        {steps.map((step) => {
          const isDone = !!completedMap[step.key];
          return (
            <div
              key={step.key}
              className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-xs ${isDone ? 'text-muted-foreground line-through' : 'hover:bg-violet-100/50'}`}
              data-testid={`gs-${storageKey}-step-${step.key}`}
            >
              {isDone
                ? <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />
                : <Circle size={14} className="text-violet-400 shrink-0" />}
              <span className="flex-1">{step.label}</span>
              {/* iter395h — Show the CTA even when the step is "done" if
                  the step is a recurring action (auto:true). Without this,
                  the user couldn't re-trigger e.g. "Record a new bank
                  payment" after clicking it once because the button
                  vanished. For auto:true steps, completion is decided by
                  isCompleted() against real data, NOT by the click. */}
              {(!isDone || step.auto) && (
                step.href ? (
                  <a href={step.href} className="inline-flex items-center gap-1 text-violet-700 hover:text-violet-900 font-medium">
                    {step.cta || 'Go'} <ChevronRight size={11} />
                  </a>
                ) : step.onClick ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[11px] text-violet-700 hover:text-violet-900 hover:bg-violet-100"
                    onClick={() => {
                      step.onClick();
                      // Only mark done on click for non-auto steps; auto
                      // steps are decided by the isCompleted() check.
                      if (!step.auto) markDone(step.key);
                    }}
                    data-testid={`gs-${storageKey}-step-${step.key}-cta`}
                  >
                    {step.cta || 'Go'} <ChevronRight size={11} />
                  </Button>
                ) : null
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
